import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft, Plus, Edit2, Trash2, Music, ListMusic, Send,
  ChevronUp, ChevronDown, X, ExternalLink,
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useCollection, useFileUpload } from '../../hooks/useFirestore'
import { deleteFile, callFunction } from '../../services/firebase'
import { formatDate } from '../../utils/helpers'
import { Badge, Card, Button, Modal, Input, Textarea, Select } from '../../components/common'

const MAX_AUDIO_BYTES = 50 * 1024 * 1024

const SongForm = ({ song, departmentId, onSave, onClose }) => {
  const [formData, setFormData] = useState(song || {
    title: '',
    artist: '',
    songKey: '',
    youtubeUrl: '',
    lyrics: '',
    notes: '',
    audioUrl: null,
    audioStoragePath: null,
    fileName: '',
    fileSize: null,
  })
  const [saving, setSaving] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const { upload, uploading } = useFileUpload()

  const handleAudioUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadError(null)
    if (file.size > MAX_AUDIO_BYTES) {
      setUploadError('Audio file must be under 50 MB.')
      return
    }
    const path = `songs/${departmentId}/${Date.now()}_${file.name}`
    const result = await upload(file, path)
    if (result.success) {
      setFormData({
        ...formData,
        audioUrl: result.url,
        audioStoragePath: path,
        fileName: file.name,
        fileSize: file.size,
      })
    } else {
      setUploadError('Upload failed — check that the file is an audio file.')
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    await onSave({ ...formData, departmentId: song?.departmentId || departmentId })
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Song Title"
          required
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          placeholder="Way Maker"
        />
        <Input
          label="Artist (optional)"
          value={formData.artist}
          onChange={(e) => setFormData({ ...formData, artist: e.target.value })}
          placeholder="Sinach"
        />
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Key (optional)"
          value={formData.songKey}
          onChange={(e) => setFormData({ ...formData, songKey: e.target.value })}
          placeholder="E"
        />
        <Input
          label="YouTube / streaming link (optional)"
          type="url"
          value={formData.youtubeUrl}
          onChange={(e) => setFormData({ ...formData, youtubeUrl: e.target.value })}
          placeholder="https://youtube.com/..."
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Practice recording (optional, audio only)
        </label>
        <div className="flex items-center gap-4 flex-wrap">
          {formData.fileName && (
            <Badge variant="info" size="sm" icon={Music}>{formData.fileName}</Badge>
          )}
          <label className="px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
            <span className="text-sm">{uploading ? 'Uploading...' : 'Upload Audio'}</span>
            <input type="file" accept="audio/*" onChange={handleAudioUpload} className="hidden" />
          </label>
        </div>
        {uploadError && <p className="text-red-500 text-sm mt-1">{uploadError}</p>}
      </div>

      <Textarea
        label="Lyrics (optional)"
        rows={6}
        value={formData.lyrics}
        onChange={(e) => setFormData({ ...formData, lyrics: e.target.value })}
        placeholder="Paste the lyrics here..."
      />

      <Input
        label="Notes (optional)"
        value={formData.notes}
        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        placeholder="Start soft, build on the bridge..."
      />

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="primary" loading={saving || uploading}>
          {song ? 'Update Song' : 'Add Song'}
        </Button>
      </div>
    </form>
  )
}

const SetlistForm = ({ setlist, songs, members, onSave, onClose }) => {
  const [serviceDate, setServiceDate] = useState(setlist?.serviceDate || '')
  const [title, setTitle] = useState(setlist?.title || '')
  const [items, setItems] = useState(setlist?.items || [])
  const [addSongId, setAddSongId] = useState('')
  const [saving, setSaving] = useState(false)

  const songOptions = [
    { value: '', label: 'Pick a song to add...' },
    ...songs
      .filter((s) => !items.some((i) => i.songId === s.id))
      .map((s) => ({ value: s.id, label: s.title })),
  ]
  const leadOptions = [
    { value: '', label: 'No lead assigned' },
    ...members.map((m) => ({ value: m.id, label: m.displayName })),
  ]

  const addSong = () => {
    const song = songs.find((s) => s.id === addSongId)
    if (!song) return
    setItems([
      ...items,
      {
        songId: song.id,
        title: song.title,
        songKey: song.songKey || '',
        youtubeUrl: song.youtubeUrl || '',
        leadUid: '',
        leadName: '',
        notes: '',
      },
    ])
    setAddSongId('')
  }

  const setItem = (idx, patch) => {
    setItems(items.map((item, i) => (i === idx ? { ...item, ...patch } : item)))
  }

  const move = (idx, dir) => {
    const next = [...items]
    const j = idx + dir
    if (j < 0 || j >= next.length) return
    ;[next[idx], next[j]] = [next[j], next[idx]]
    setItems(next)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!serviceDate || items.length === 0) return
    setSaving(true)
    await onSave({
      serviceDate,
      title,
      items: items.map((item, i) => ({ ...item, order: i })),
    })
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <Input
          label="Service / practice date"
          type="date"
          required
          value={serviceDate}
          onChange={(e) => setServiceDate(e.target.value)}
        />
        <Input
          label="Title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Sunday Worship"
        />
      </div>

      <div>
        <p className="text-sm font-medium text-gray-700 mb-2">Songs ({items.length})</p>
        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={`${item.songId}-${i}`} className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="font-medium text-dark-100">
                  <span className="text-gray-400 mr-2">{i + 1}.</span>
                  {item.title}
                  {item.songKey && <span className="text-sm text-gray-500 ml-2">({item.songKey})</span>}
                </p>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                    className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1}
                    className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-30">
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-2 mt-2">
                <Select
                  options={leadOptions}
                  value={item.leadUid}
                  onChange={(e) => {
                    const member = members.find((m) => m.id === e.target.value)
                    setItem(i, {
                      leadUid: e.target.value,
                      leadName: member?.displayName || '',
                    })
                  }}
                />
                <Input
                  placeholder="Notes for this song (optional)"
                  value={item.notes}
                  onChange={(e) => setItem(i, { notes: e.target.value })}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mt-3">
          <div className="flex-1">
            <Select options={songOptions} value={addSongId} onChange={(e) => setAddSongId(e.target.value)} />
          </div>
          <Button type="button" variant="outline" icon={Plus} onClick={addSong} disabled={!addSongId}>
            Add
          </Button>
        </div>
        {songs.length === 0 && (
          <p className="text-sm text-gray-500 mt-2">
            The song library is empty — add songs on the Songs tab first.
          </p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="primary" loading={saving} disabled={items.length === 0 || !serviceDate}>
          {setlist ? 'Save Changes' : 'Save as Draft'}
        </Button>
      </div>
    </form>
  )
}

const SongsManage = () => {
  const { user, isEditor } = useAuth()
  const [tab, setTab] = useState('setlists')
  const [selectedDept, setSelectedDept] = useState(null)
  const [songModal, setSongModal] = useState(null)        // { song } | 'new'
  const [setlistModal, setSetlistModal] = useState(null)  // { setlist } | 'new'
  const [publishConfirm, setPublishConfirm] = useState(null)
  const [deleteSong, setDeleteSong] = useState(null)
  const [deleteSetlist, setDeleteSetlist] = useState(null)
  const [notifyResult, setNotifyResult] = useState(null)
  const [notifying, setNotifying] = useState(null)

  const { data: departments } = useCollection('departments', {
    orderByField: 'order',
    orderDirection: 'asc',
  })

  const manageable = useMemo(
    () =>
      (departments || []).filter(
        (d) => isEditor || (d.leaderUids || []).includes(user?.uid)
      ),
    [departments, isEditor, user]
  )

  const dept = selectedDept || manageable[0]?.id || '__none__'

  const { data: songs, add: addSong, update: updateSong, remove: removeSong } = useCollection('songs', {
    orderByField: 'title',
    orderDirection: 'asc',
    whereClause: { field: 'departmentId', operator: '==', value: dept },
  })

  const { data: setlists, add: addSetlist, update: updateSetlist, remove: removeSetlist } = useCollection('setlists', {
    orderByField: 'serviceDate',
    orderDirection: 'desc',
    whereClause: { field: 'departmentId', operator: '==', value: dept },
  })

  const { data: members } = useCollection(`departments/${dept}/members`, {
    orderByField: 'displayName',
    orderDirection: 'asc',
  })

  const notify = async (setlistId) => {
    setNotifying(setlistId)
    setNotifyResult(null)
    try {
      const result = await callFunction('notifySetlistPublished', { setlistId })
      setNotifyResult({
        ok: true,
        message: `Notified ${result.sent} member${result.sent === 1 ? '' : 's'} by email` +
          (result.smsSent ? ` and ${result.smsSent} by text` : '') +
          (result.failed ? ` (${result.failed} failed)` : '') + '.',
      })
    } catch (err) {
      setNotifyResult({
        ok: false,
        message: `Notification failed: ${err.message}. Use "Send notifications" to retry.`,
      })
    } finally {
      setNotifying(null)
    }
  }

  const handlePublish = async () => {
    const setlist = publishConfirm
    setPublishConfirm(null)
    await updateSetlist(setlist.id, {
      status: 'published',
      publishedAt: new Date().toISOString(),
    })
    await notify(setlist.id)
  }

  const handleSaveSong = async (data) => {
    if (songModal?.id) {
      await updateSong(songModal.id, data)
    } else {
      await addSong({ ...data, createdBy: user.uid })
    }
    setSongModal(null)
  }

  const handleDeleteSong = async () => {
    if (!deleteSong) return
    if (deleteSong.audioStoragePath) {
      try {
        await deleteFile(deleteSong.audioStoragePath)
      } catch (err) {
        console.warn('Could not delete audio file:', err)
      }
    }
    await removeSong(deleteSong.id)
    setDeleteSong(null)
  }

  const handleSaveSetlist = async (data) => {
    if (setlistModal?.id) {
      await updateSetlist(setlistModal.id, data)
    } else {
      await addSetlist({ ...data, departmentId: dept, status: 'draft', createdBy: user.uid })
    }
    setSetlistModal(null)
  }

  const songUsedIn = (songId) =>
    (setlists || []).filter((sl) => (sl.items || []).some((i) => i.songId === songId)).length

  if (departments && manageable.length === 0) {
    return (
      <Card className="text-center py-12 text-gray-500">
        <Music className="w-12 h-12 mx-auto mb-2 opacity-50" />
        <p className="mb-4">You're not a department leader. Ask an admin to make you one.</p>
        <Link to="/workers/songs" className="text-primary-600 hover:text-primary-700 font-medium">
          Back to Songs
        </Link>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link
            to="/workers/songs"
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Songs
          </Link>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Manage Songs</h1>
        </div>
        {manageable.length > 1 && (
          <div className="w-48">
            <Select
              options={manageable.map((d) => ({ value: d.id, label: d.name }))}
              value={dept}
              onChange={(e) => setSelectedDept(e.target.value)}
            />
          </div>
        )}
      </div>

      {notifyResult && (
        <div className={`rounded-lg p-4 text-sm ${notifyResult.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`}>
          {notifyResult.message}
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        {[
          { key: 'setlists', label: 'Song Lists' },
          { key: 'songs', label: 'Song Library' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              tab === t.key
                ? 'bg-primary-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'songs' ? (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button variant="primary" icon={Plus} onClick={() => setSongModal('new')}>
              Add Song
            </Button>
          </div>
          {(songs || []).map((song) => (
            <div key={song.id} className="bg-white rounded-xl shadow-soft p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-dark-100">{song.title}</p>
                <div className="flex items-center gap-2 flex-wrap text-sm text-gray-500">
                  {song.artist && <span>{song.artist}</span>}
                  {song.songKey && <Badge variant="secondary" size="sm">Key: {song.songKey}</Badge>}
                  {song.audioUrl && <Badge variant="info" size="sm">Audio</Badge>}
                  {song.youtubeUrl && (
                    <a href={song.youtubeUrl} target="_blank" rel="noopener noreferrer"
                      className="text-primary-600 hover:text-primary-700 inline-flex items-center gap-1">
                      Listen <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => setSongModal(song)}
                  className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg">
                  <Edit2 className="w-5 h-5" />
                </button>
                <button onClick={() => setDeleteSong(song)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
          {(songs || []).length === 0 && (
            <Card className="text-center py-12 text-gray-500">
              <Music className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No songs yet — add your first one</p>
            </Card>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex justify-end">
            <Button variant="primary" icon={Plus} onClick={() => setSetlistModal('new')}
              disabled={(songs || []).length === 0}>
              New Song List
            </Button>
          </div>
          {(setlists || []).map((sl) => (
            <div key={sl.id} className="bg-white rounded-xl shadow-soft p-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-medium text-dark-100">
                    {formatDate(sl.serviceDate, 'EEEE, MMMM d, yyyy')}
                    {sl.title ? ` — ${sl.title}` : ''}
                  </p>
                  <div className="flex items-center gap-2 flex-wrap mt-1">
                    <Badge variant={sl.status === 'published' ? 'success' : 'gray'} size="sm">
                      {sl.status === 'published' ? 'Published' : 'Draft'}
                    </Badge>
                    <span className="text-sm text-gray-500">
                      {(sl.items || []).length} song{(sl.items || []).length === 1 ? '' : 's'}
                    </span>
                    {sl.notifiedAt && (
                      <Badge variant="info" size="sm">
                        Notified{typeof sl.notifyCount === 'number' ? ` (${sl.notifyCount})` : ''}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {sl.status === 'draft' && (
                    <Button variant="primary" size="sm" icon={Send} onClick={() => setPublishConfirm(sl)}>
                      Publish & Notify
                    </Button>
                  )}
                  {sl.status === 'published' && !sl.notifiedAt && (
                    <Button variant="primary" size="sm" icon={Send}
                      loading={notifying === sl.id} onClick={() => notify(sl.id)}>
                      Send notifications
                    </Button>
                  )}
                  <button onClick={() => setSetlistModal(sl)}
                    className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg">
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button onClick={() => setDeleteSetlist(sl)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <ol className="mt-3 space-y-1">
                {(sl.items || []).map((item, i) => (
                  <li key={`${item.songId}-${i}`} className="text-sm text-gray-600">
                    {i + 1}. {item.title}
                    {item.leadName && <span className="text-amber-700"> — lead: {item.leadName}</span>}
                  </li>
                ))}
              </ol>
            </div>
          ))}
          {(setlists || []).length === 0 && (
            <Card className="text-center py-12 text-gray-500">
              <ListMusic className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No song lists yet{(songs || []).length === 0 ? ' — add songs to the library first' : ''}</p>
            </Card>
          )}
        </div>
      )}

      {/* Song add/edit */}
      <Modal
        isOpen={!!songModal}
        onClose={() => setSongModal(null)}
        title={songModal === 'new' ? 'Add Song' : 'Edit Song'}
        size="lg"
      >
        {songModal && (
          <SongForm
            song={songModal === 'new' ? null : songModal}
            departmentId={dept}
            onSave={handleSaveSong}
            onClose={() => setSongModal(null)}
          />
        )}
      </Modal>

      {/* Setlist add/edit */}
      <Modal
        isOpen={!!setlistModal}
        onClose={() => setSetlistModal(null)}
        title={setlistModal === 'new' ? 'New Song List' : 'Edit Song List'}
        size="lg"
      >
        {setlistModal && (
          <SetlistForm
            setlist={setlistModal === 'new' ? null : setlistModal}
            songs={songs || []}
            members={members || []}
            onSave={handleSaveSetlist}
            onClose={() => setSetlistModal(null)}
          />
        )}
      </Modal>

      {/* Publish confirm */}
      <Modal
        isOpen={!!publishConfirm}
        onClose={() => setPublishConfirm(null)}
        title="Publish & Notify"
        size="sm"
      >
        <p className="text-gray-600 mb-6">
          Publish this song list for {publishConfirm ? formatDate(publishConfirm.serviceDate, 'MMMM d') : ''}?
          Every member of this department will immediately get an email
          {` `}and a text (if they opted in) asking them to learn the songs.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setPublishConfirm(null)}>Cancel</Button>
          <Button variant="primary" icon={Send} onClick={handlePublish}>Publish & Notify</Button>
        </div>
      </Modal>

      {/* Delete song confirm */}
      <Modal isOpen={!!deleteSong} onClose={() => setDeleteSong(null)} title="Delete Song" size="sm">
        <p className="text-gray-600 mb-6">
          Delete "{deleteSong?.title}"?
          {deleteSong?.audioStoragePath ? ' Its uploaded audio will also be removed.' : ''}
          {deleteSong && songUsedIn(deleteSong.id) > 0
            ? ` It appears in ${songUsedIn(deleteSong.id)} song list(s) — those entries will keep the song's name but lose lyrics/audio.`
            : ''}
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteSong(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDeleteSong}>Delete</Button>
        </div>
      </Modal>

      {/* Delete setlist confirm */}
      <Modal isOpen={!!deleteSetlist} onClose={() => setDeleteSetlist(null)} title="Delete Song List" size="sm">
        <p className="text-gray-600 mb-6">
          Delete the song list for {deleteSetlist ? formatDate(deleteSetlist.serviceDate, 'MMMM d, yyyy') : ''}?
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteSetlist(null)}>Cancel</Button>
          <Button variant="danger" onClick={async () => { await removeSetlist(deleteSetlist.id); setDeleteSetlist(null) }}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  )
}

export default SongsManage
